// Services Data
export const services = [
    {
        id: 'training',
        eyebrow: 'AI Software Training',
        title: 'Fundamentals of AI for Business & Professionals',
        description: 'A structured, business-focused AI program designed to help professionals and organizations understand, adopt, and apply AI responsibly in today\'s workplace.',
        longDescription: 'This program equips leaders, managers, and teams with the essential knowledge, practical frameworks, and confidence to work effectively with AI—without requiring technical backgrounds. Participants learn how AI is transforming business, how to use it ethically, and how to integrate it into real tasks that improve productivity and decision-making. Through a combination of strategic insight and hands-on practice, we help organizations move beyond experimentation to meaningful, sustainable AI adoption—aligned with governance, risk awareness, and business objectives.',
        features: [
            'Clear understanding of AI and its impact on modern business',
            'Practical frameworks for responsible AI adoption',
            'Improved productivity through AI-supported task automation',
            'Better decision-making using AI tools and insights',
            'Strong awareness of governance, ethics, and risk',
            'A personal and organizational AI integration roadmap'
        ],
        image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop',
        tags: ['Training', 'Workshops', 'Certification'],
        resourcePdf: {
            title: 'Fundamentals of AI for Business and Professionals',
            url: '/resource/Fundamentals of AI for Business and Professionals - PhoenixAI.pdf'
        },
    },
    {
        id: 'iso27001',
        eyebrow: 'Information Security Management',
        title: 'ISO/IEC 27001:2022 Consultancy Program',
        description: 'A structured, risk-based consultancy program designed to help organizations build, implement, and sustain an audit-ready Information Security Management System (ISMS) aligned with ISO/IEC 27001:2022.',
        longDescription: 'This program supports organizations through the full ISMS lifecycle—from defining scope and assessing risks to establishing controls, documentation, and governance structures that meet international standards. Our approach focuses on practical implementation, ensuring that security frameworks are not only compliant, but also effective, sustainable, and aligned with business operations. We work closely with your leadership and operational teams to strengthen information security practices while building confidence for certification readiness and long-term resilience.',
        features: [
            'ISMS scope definition and structured gap assessment',
            'Risk assessment, treatment planning, and control design',
            'Development of security policies, procedures, and records',
            'Statement of Applicability (SoA) preparation',
            'ISMS training, awareness, and governance alignment',
            'Internal audit support and certification readiness guidance'
        ],
        image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop',
        tags: ['ISO 27001', 'Information Security', 'Certification'],
        resourcePdf: {
            title: 'ISO 27001 Consultancy Program',
            url: '/resource/ISO 27001 Consultancy program.pdf'
        },
        programDetails: {
            standard: 'ISO/IEC 27001:2022',
            programType: 'ISMS Consultancy',
            engagementScope: ['Planning', 'Documentation', 'Implementation', 'Training', 'Internal Audit', 'Certification Readiness'],
            idealFor: [
                'Cybersecurity & compliance-driven business',
                'Organizations seeking ISO 27001 certification',
                'Technology & consulting partners',
                'SMEs & Enterprise'
            ],
            highlights: [
                'Aligned with ISO/IEC 27001:2022',
                'Practical & risk-based approach',
                'Audit-focused, evidence-driven',
                'Internal audit & management review support'
            ],
            consultancyScope: [
                {
                    phase: 'Project Kick-off & Governance',
                    details: 'Project milestones, deliverables, and roles; ISMS team alignment and responsibility assignment; Confirmation of ISMS scope and boundaries'
                },
                {
                    phase: 'Gap Assessment',
                    details: 'Review of existing policies, processes, and controls; Clause-by-clause and Annex A assessment; Detailed gap assessment report with recommendations'
                },
                {
                    phase: 'ISMS Awareness Training',
                    details: 'Organization-wide ISMS awareness program; Overview of ISO/IEC 27001:2022 clauses and Annex A controls'
                },
                {
                    phase: 'ISMS Framework Development',
                    details: 'Information Security Policy & governance framework; Risk assessment and treatment methodology; Asset inventory master list; Statement of Applicability (SoA); Define roles, responsibilities, and compliance functions'
                },
                {
                    phase: 'Risk Assessment & Control Design',
                    details: 'Facilitated risk assessment workshops; Control selection and justification; Due diligence processes for suppliers and third parties'
                },
                {
                    phase: 'Internal Audit & Management Review Preparation',
                    details: 'Internal audit support and findings resolution; Management review meeting preparation; Final readiness for certification audit'
                }
            ],
            contactInfo: {
                contact1: {
                    name: 'Yn Leong',
                    phone: '+6012-377 3589',
                    email: 'ynleong@phoenixai.my'
                },
                contact2: {
                    name: 'Amin Sya',
                    phone: '+6013-662 9189',
                    email: 'aminsya@phoenixai.my'
                },
                company: 'Phoenix Ai Sdn Bhd (1631340-A)',
                address: 'M2-05-05, Menara 2, 8Trium, Jalan Cempaka SD 12/5, Bandar Sri Damansara, 52200, Kuala Lumpur'
            }
        }
    },
    {
        id: 'iso9001',
        eyebrow: 'Quality Management',
        title: 'ISO 9001:2015 Consultancy Program',
        description: 'A structured consultancy program designed to help organizations build a strong, sustainable, and audit-ready Quality Management System (QMS) aligned with ISO 9001:2015.',
        longDescription: 'This program guides organizations through the full QMS lifecycle—from defining business context and process mapping to risk-based thinking and performance monitoring. Our approach focuses on practical implementation, ensuring that quality systems are not only compliant, but also effective, scalable, and aligned with real operational needs. We work closely with leadership and operational teams to embed quality into everyday processes—strengthening governance, improving consistency, and building long-term organizational resilience.',
        features: [
            'QMS planning, documentation, and implementation',
            'Process mapping and risk-based thinking approach',
            'Development of quality policies, procedures, and records',
            'Risk assessment and treatment for operational improvement',
            'Internal audit preparation and certification readiness',
            'Management review support and continuous improvement guidance'
        ],
        image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop',
        tags: ['ISO 9001', 'Quality Management', 'Certification'],
        resourcePdf: {
            title: 'ISO 9001 Consultancy Program',
            url: '/resource/ISO 9001 Consultancy program.pdf'
        },
        programDetails: {
            standard: 'ISO 9001:2015',
            programType: 'QMS Consultancy',
            engagementScope: ['Planning', 'Documentation', 'Implementation', 'Training', 'Internal Audit', 'Certification Readiness'],
            idealFor: [
                'Quality & operations-driven businesses',
                'Organizations seeking ISO 9001 certification',
                'Technology & consulting partners',
                'SMEs & Enterprise'
            ],
            highlights: [
                'Aligned with ISO 9001:2015',
                'Process-driven & practical approach',
                'Audit-focused & business-oriented',
                'Internal audit & management review support'
            ],
            consultancyScope: [
                {
                    phase: 'Project Kick-off & Governance',
                    details: 'Project milestones, deliverables, and roles; QMS team alignment and responsibility assignment; Confirmation of QMS scope and boundaries'
                },
                {
                    phase: 'QMS Awareness Training',
                    details: 'Organization-wide QMS awareness program; Overview of ISO 9001:2015 clauses and requirements'
                },
                {
                    phase: 'QMS Framework Development',
                    details: 'Quality Policy and objectives; Process mapping and interaction model; Risk-based thinking methodology; Documented procedures and records; Defined roles, responsibilities, and quality functions'
                },
                {
                    phase: 'Risk Assessment & Treatment',
                    details: 'Facilitated quality risk assessment workshops; Development of appropriate risk treatment actions'
                },
                {
                    phase: 'Internal Audit & Management Review Preparation',
                    details: 'Internal audit support and findings resolution; Management review meeting preparation; Final readiness for certification audit'
                }
            ],
            registrationAuditPhases: [
                'Initial Certification Audit (Year 1)',
                'Surveillance Audit 1 (Year 2)',
                'Surveillance Audit 2 (Year 3)'
            ],
            contactInfo: {
                contact1: {
                    name: 'Yn Leong',
                    phone: '+6012-377 3589',
                    email: 'ynleong@phoenixai.my'
                },
                contact2: {
                    name: 'Amin Sya',
                    phone: '+6013-662 9189',
                    email: 'aminsya@phoenixai.my'
                },
                company: 'Phoenix Ai Sdn Bhd (1631340-A)',
                address: 'M2-05-05, Menara 2, 8Trium, Jalan Cempaka SD 12/5, Bandar Sri Damansara, 52200, Kuala Lumpur'
            }
        }
    },
    {
        id: 'cctv',
        eyebrow: 'Security Platform',
        title: 'AI-Powered CCTV Prevention Platform',
        description: 'An intelligent security platform designed to help organizations move from reactive surveillance to proactive prevention—using AI to detect risks, analyze behavior, and support faster decision-making.',
        longDescription: 'Our platform enhances traditional CCTV systems with real-time analytics that identify potential threats before incidents escalate. Built for commercial and enterprise environments, the solution integrates seamlessly with existing security infrastructure while maintaining strong governance and data-handling standards. By combining visibility, automation, and control, we help security teams strengthen situational awareness and respond with confidence—without compromising compliance or operational efficiency.',
        features: [
            'Real-time threat detection',
            'Enterprise system integration',
            'Customizable alert thresholds',
            'Behavioral anomaly analysis',
            'Regulatory-compliant data handling',
            '24/7 monitoring capabilities'
        ],
        image: 'https://images.unsplash.com/photo-1557597774-9d273605dfa9?w=600&h=400&fit=crop',
        tags: ['CCTV', 'Video Analytics', 'Security'],
        resourcePdf: {
            title: 'Intelligent Campus Security Solution',
            url: '/resource/eSurfing_SeeLink_Intelligent_Campus_Security 24112025v1.4.pdf'
        },
    },
    {
        id: 'ascend-ai-audit',
        eyebrow: 'AI Governance Audit',
        title: 'ASCEND AI Readiness Audit',
        description: 'A leadership-grade audit that assesses whether your organisation is truly ready to deploy Agentic AI safely, responsibly, and at scale—aligned with Singapore\'s Agentic AI Governance Framework.',
        longDescription: 'The ASCEND AI Readiness Audit goes beyond tools, models, and policies to evaluate who is accountable when AI acts, how AI autonomy is bounded and governed, where human judgement must remain in the loop, whether roles are redesigned for AI supervision, and how risk, ethics, and outcomes are actually managed. This is not a technical penetration test—it is a leadership and operating model audit. Within 14–21 days, receive an ASCEND AI Readiness Scorecard, Leadership & Accountability Risk Map, Agentic AI Exposure & Gap Analysis, Prioritised 30–90 Day Action Roadmap, and Executive Briefing suitable for Board and ExCo forums.',
        features: [
            'ASCEND AI Readiness Scorecard',
            'Leadership & Accountability Risk Map',
            'Agentic AI Exposure & Gap Analysis',
            'Prioritised 30–90 Day Action Roadmap',
            'Executive Briefing (Board / ExCo Ready)',
            'Aligned with Singapore\'s Agentic AI Governance Framework'
        ],
        image: 'https://images.pexels.com/photos/7652046/pexels-photo-7652046.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
        tags: ['AI Governance', 'Audit', 'Leadership', 'Agentic AI'],
        resourcePdf: {
            title: 'ASCEND AI Readiness Audit',
            url: '/resource/ASCEND AI Readiness Audit - PhoenixAI.pdf'
        },
        auditDetails: {
            framework: 'ASCEND Method',
            duration: '2–3 weeks',
            engagementFormat: ['Leadership interviews', 'Diagnostic assessment', 'Document review'],
            idealFor: [
                'Boards and Audit Committees',
                'CEOs and Executive Leadership Teams',
                'CIOs, CDOs, Transformation and Risk Leaders',
                'Organisations planning or piloting Agentic AI'
            ],
            sectors: [
                'Finance',
                'Healthcare',
                'HR',
                'Public Sector',
                'Professional & Advisory Services'
            ],
            ascendFramework: [
                {
                    letter: 'A',
                    word: 'Align',
                    description: 'Clear purpose and named human ownership for every AI-enabled task'
                },
                {
                    letter: 'S',
                    word: 'Systemize',
                    description: 'Defined scope, permissions, and escalation paths for AI agents'
                },
                {
                    letter: 'C',
                    word: 'Cultivate',
                    description: 'Clear rules for where trust, empathy, and judgement remain human'
                },
                {
                    letter: 'E',
                    word: 'Empower',
                    description: 'Roles redesigned so people supervise AI, not chase tasks'
                },
                {
                    letter: 'N',
                    word: 'Navigate',
                    description: 'Ethical, reputational, and operational risk awareness built into decisions'
                },
                {
                    letter: 'D',
                    word: 'Deliver',
                    description: 'Outcomes, overrides, failures, and learning are measured and improved'
                }
            ],
            contactInfo: {
                contact1: {
                    name: 'Jenny Leong',
                    phone: '+6012-377 3589',
                    email: 'ynleong@phoenixai.my'
                },
                contact2: {
                    name: 'Aminsya',
                    phone: '+6013-662 9189',
                    email: 'aminsya@phoenixai.my'
                },
                company: 'Phoenix Ai Sdn Bhd (1631340-A)',
                address: 'M2-05-05, Menara 2, 8Trium, Jalan Cempaka SD 12/5, Bandar Sri Damansara, 52200, Kuala Lumpur'
            }
        }
    },
];

export default services;
